import { FocusScope } from '@react-aria/focus';
import cx from 'classnames';
import { useCallback, useState, type FunctionComponent } from 'react';
import FocusLock from 'react-focus-lock';
import { useIntl } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { DynamicButton } from '@trello/dynamic-tokens';
import { useFeatureGate } from '@trello/feature-gate-client';
import { CloseIcon } from '@trello/nachos/icons/close';
import { Overlay } from '@trello/nachos/overlay';
import { useSplitScreenSharedState } from '@trello/split-screen';
import type { PPOnboardingModalTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { LazyPPOnboardingModalScreen } from './LazyPPOnboardingModalScreen';

import styles from './PPOnboardingModal.less';

const noop = () => {};

export const PPOnboardingModal: FunctionComponent<{
  onComplete: () => void;
}> = ({ onComplete }) => {
  const { value: isReactFocusLockEnabled } = useFeatureGate(
    'ghost_use_react_focus_lock',
  );
  const intl = useIntl();
  const [activeScreen, setActiveScreen] = useState(0);
  const [latestScreenViewed, setLatestScreenViewed] = useState(0);
  const { dismissOneTimeMessage } = useOneTimeMessagesDismissed();
  const { toggleInbox, panels } = useSplitScreenSharedState();

  const completeOnboarding = useCallback(() => {
    if (!panels.inbox) {
      toggleInbox();
    }

    dismissOneTimeMessage('split-screen-onboarding');

    Analytics.sendTrackEvent({
      action: 'completed',
      actionSubject: 'onboarding',
      actionSubjectId: 'personalProductivitySplitScreenOnboarding',
      source: 'splitScreenOnboardingEndScreen',
    });

    onComplete();
  }, [dismissOneTimeMessage, onComplete, panels.inbox, toggleInbox]);

  const onClose = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'ppOnboardingModalCloseButton',
      source: 'ppOnboardingModal',
    });
    completeOnboarding();
  }, [completeOnboarding]);

  const onOnboardingComplete = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'ppOnboardingModalRevealButton',
      source: 'ppOnboardingModal',
    });
    completeOnboarding();
  }, [completeOnboarding]);

  const setScreenState = useCallback(
    (screen: number) => {
      setActiveScreen(screen);
      if (screen > latestScreenViewed) {
        setLatestScreenViewed(screen);
      }
    },
    [latestScreenViewed],
  );

  const advanceToNextScreen = useCallback(() => {
    if (activeScreen < 3) {
      setScreenState(activeScreen + 1);
    }
  }, [activeScreen, setScreenState]);

  const sendProgressIndicatorClickEvent = useCallback(
    (screen: number) => {
      Analytics.sendClickedButtonEvent({
        buttonName: 'ppOnboardingModalProgressIndicator',
        source: 'ppOnboardingModal',
        attributes: {
          isRewatch: screen < latestScreenViewed,
          isSkipForward: screen > activeScreen,
        },
      });
    },
    [activeScreen, latestScreenViewed],
  );

  const onClickFirstScreenProgressIndicator = useCallback(() => {
    sendProgressIndicatorClickEvent(0);
    setScreenState(0);
  }, [sendProgressIndicatorClickEvent, setScreenState]);

  const onClickSecondScreenProgressIndicator = useCallback(() => {
    sendProgressIndicatorClickEvent(1);
    setScreenState(1);
  }, [sendProgressIndicatorClickEvent, setScreenState]);

  const onClickThirdScreenProgressIndicator = useCallback(() => {
    sendProgressIndicatorClickEvent(2);

    // If you're not yet to the Third screen, only advance to the next screen
    // If you're beyond the Third screen, go back to the Third screen
    // If you're on the Third screen, do nothing
    if (activeScreen < 2) {
      advanceToNextScreen();
    } else if (latestScreenViewed > 2) {
      setScreenState(2);
    }
  }, [
    activeScreen,
    advanceToNextScreen,
    latestScreenViewed,
    sendProgressIndicatorClickEvent,
    setScreenState,
  ]);

  const onClickFourthScreenProgressIndicator = useCallback(() => {
    sendProgressIndicatorClickEvent(3);

    // If you're not yet to the Fourth screen, only advance to the next screen
    // If you're on the Fourth screen, do nothing
    if (activeScreen !== 3) {
      advanceToNextScreen();
    }
  }, [activeScreen, advanceToNextScreen, sendProgressIndicatorClickEvent]);

  return (
    <Overlay
      className={styles.overlayOverrides}
      closeOnEscape={false}
      onClose={noop}
    >
      {isReactFocusLockEnabled ? (
        <FocusLock returnFocus={true} autoFocus={true}>
          <div className={styles.overlay}>
            <div className={styles.modalNavigation}>
              <button
                className={cx(styles.progressIndicator, {
                  [styles.activeProgressIndicator]: activeScreen === 0,
                  [styles.filledProgressIndicator]: activeScreen > 0,
                })}
                data-testid={getTestId<PPOnboardingModalTestIds>(
                  'first-screen-progress-indicator',
                )}
                onClick={onClickFirstScreenProgressIndicator}
                aria-label={intl.formatMessage({
                  id: 'templates.split_screen.pp-beta-onboarding.capture-everything',
                  defaultMessage: 'Capture everything with Inbox',
                  description:
                    'Aria label for the first progress indicator button for capturing everything with inbox',
                })}
              >
                <div className={styles.progressIndicatorBackground}>
                  <div
                    className={cx(
                      styles.progressIndicatorInner,
                      styles.firstProgressIndicator,
                    )}
                  />
                </div>
              </button>
              <button
                className={cx(styles.progressIndicator, {
                  [styles.activeProgressIndicator]: activeScreen === 1,
                  [styles.filledProgressIndicator]: activeScreen > 1,
                })}
                data-testid={getTestId<PPOnboardingModalTestIds>(
                  'second-screen-progress-indicator',
                )}
                onClick={onClickSecondScreenProgressIndicator}
                aria-label={intl.formatMessage({
                  id: 'templates.split_screen.pp-beta-onboarding.schedule-with-planner',
                  defaultMessage: 'Schedule your to-dos with Planner',
                  description:
                    'Aria label for the second progress indicator button for scheduling your to-dos with planner',
                })}
              >
                <div className={styles.progressIndicatorBackground}>
                  <div
                    className={cx(
                      styles.progressIndicatorInner,
                      styles.secondProgressIndicator,
                    )}
                  />
                </div>
              </button>
              <button
                className={cx(styles.progressIndicator, {
                  [styles.activeProgressIndicator]: activeScreen === 2,
                  [styles.filledProgressIndicator]: activeScreen > 2,
                })}
                data-testid={getTestId<PPOnboardingModalTestIds>(
                  'third-screen-progress-indicator',
                )}
                onClick={onClickThirdScreenProgressIndicator}
                aria-label={intl.formatMessage({
                  id: 'templates.split_screen.pp-beta-onboarding.drag-drop-get-it-done',
                  defaultMessage: 'Drag, drop, get it done',
                  description:
                    'Aria label for the third progress indicator button for drag, drop, get it done',
                })}
              >
                <div className={styles.progressIndicatorBackground}>
                  <div
                    className={cx(
                      styles.progressIndicatorInner,
                      styles.thirdProgressIndicator,
                    )}
                  />
                </div>
              </button>
              <button
                className={cx(styles.progressIndicator, {
                  [styles.activeProgressIndicator]: activeScreen === 3,
                })}
                data-testid={getTestId<PPOnboardingModalTestIds>(
                  'fourth-screen-progress-indicator',
                )}
                onClick={onClickFourthScreenProgressIndicator}
                aria-label={intl.formatMessage({
                  id: 'templates.split_screen.pp-beta-onboarding.show-or-hide-panels',
                  defaultMessage: 'Show or hide panels',
                  description:
                    'Aria label for the fourth progress indicator button for show or hide panels',
                })}
              >
                <div className={styles.progressIndicatorBackground}>
                  <div
                    className={cx(
                      styles.progressIndicatorInner,
                      styles.fourthProgressIndicator,
                    )}
                  />
                </div>
              </button>
              <DynamicButton
                className={styles.closeButton}
                iconBefore={<CloseIcon size="medium" />}
                onClick={onClose}
                aria-label={intl.formatMessage({
                  id: 'templates.dialog_close_button.close-dialog-label',
                  defaultMessage: 'Close dialog',
                  description: 'Aria label for the close dialog button',
                })}
                data-testid={getTestId<PPOnboardingModalTestIds>(
                  'pp-onboarding-modal-close-button',
                )}
              />
            </div>

            <LazyPPOnboardingModalScreen
              activeScreen={activeScreen}
              onAnimationComplete={advanceToNextScreen}
              onOnboardingComplete={onOnboardingComplete}
            />
          </div>
        </FocusLock>
      ) : (
        <FocusScope contain restoreFocus autoFocus>
          <div className={styles.overlay}>
            <div className={styles.modalNavigation}>
              <button
                className={cx(styles.progressIndicator, {
                  [styles.activeProgressIndicator]: activeScreen === 0,
                  [styles.filledProgressIndicator]: activeScreen > 0,
                })}
                data-testid={getTestId<PPOnboardingModalTestIds>(
                  'first-screen-progress-indicator',
                )}
                onClick={onClickFirstScreenProgressIndicator}
                aria-label={intl.formatMessage({
                  id: 'templates.split_screen.pp-beta-onboarding.capture-everything',
                  defaultMessage: 'Capture everything with Inbox',
                  description:
                    'Aria label for the first progress indicator button for capturing everything with inbox',
                })}
              >
                <div className={styles.progressIndicatorBackground}>
                  <div
                    className={cx(
                      styles.progressIndicatorInner,
                      styles.firstProgressIndicator,
                    )}
                  />
                </div>
              </button>
              <button
                className={cx(styles.progressIndicator, {
                  [styles.activeProgressIndicator]: activeScreen === 1,
                  [styles.filledProgressIndicator]: activeScreen > 1,
                })}
                data-testid={getTestId<PPOnboardingModalTestIds>(
                  'second-screen-progress-indicator',
                )}
                onClick={onClickSecondScreenProgressIndicator}
                aria-label={intl.formatMessage({
                  id: 'templates.split_screen.pp-beta-onboarding.schedule-with-planner',
                  defaultMessage: 'Schedule your to-dos with Planner',
                  description:
                    'Aria label for the second progress indicator button for scheduling your to-dos with planner',
                })}
              >
                <div className={styles.progressIndicatorBackground}>
                  <div
                    className={cx(
                      styles.progressIndicatorInner,
                      styles.secondProgressIndicator,
                    )}
                  />
                </div>
              </button>
              <button
                className={cx(styles.progressIndicator, {
                  [styles.activeProgressIndicator]: activeScreen === 2,
                  [styles.filledProgressIndicator]: activeScreen > 2,
                })}
                data-testid={getTestId<PPOnboardingModalTestIds>(
                  'third-screen-progress-indicator',
                )}
                onClick={onClickThirdScreenProgressIndicator}
                aria-label={intl.formatMessage({
                  id: 'templates.split_screen.pp-beta-onboarding.drag-drop-get-it-done',
                  defaultMessage: 'Drag, drop, get it done',
                  description:
                    'Aria label for the third progress indicator button for drag, drop, get it done',
                })}
              >
                <div className={styles.progressIndicatorBackground}>
                  <div
                    className={cx(
                      styles.progressIndicatorInner,
                      styles.thirdProgressIndicator,
                    )}
                  />
                </div>
              </button>
              <button
                className={cx(styles.progressIndicator, {
                  [styles.activeProgressIndicator]: activeScreen === 3,
                })}
                data-testid={getTestId<PPOnboardingModalTestIds>(
                  'fourth-screen-progress-indicator',
                )}
                onClick={onClickFourthScreenProgressIndicator}
                aria-label={intl.formatMessage({
                  id: 'templates.split_screen.pp-beta-onboarding.show-or-hide-panels',
                  defaultMessage: 'Show or hide panels',
                  description:
                    'Aria label for the fourth progress indicator button for show or hide panels',
                })}
              >
                <div className={styles.progressIndicatorBackground}>
                  <div
                    className={cx(
                      styles.progressIndicatorInner,
                      styles.fourthProgressIndicator,
                    )}
                  />
                </div>
              </button>
              <DynamicButton
                className={styles.closeButton}
                iconBefore={<CloseIcon size="medium" />}
                onClick={onClose}
                aria-label={intl.formatMessage({
                  id: 'templates.dialog_close_button.close-dialog-label',
                  defaultMessage: 'Close dialog',
                  description: 'Aria label for the close dialog button',
                })}
                data-testid={getTestId<PPOnboardingModalTestIds>(
                  'pp-onboarding-modal-close-button',
                )}
              />
            </div>
            <LazyPPOnboardingModalScreen
              activeScreen={activeScreen}
              onAnimationComplete={advanceToNextScreen}
              onOnboardingComplete={onOnboardingComplete}
            />
          </div>
        </FocusScope>
      )}
    </Overlay>
  );
};
